import { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { lotteryApi } from '../../api/lottery.js';
import { ApiError } from '../../api/client.js';
import type { Category } from '../../api/types.js';
import { Spinner, ErrorBox, EmptyBox, Pagination, Field, SortableTh, SearchInput } from '../../components/ui.js';
import { useUrlUpdate, useUrlSearch } from '../../lib/hooks.js';
import { text, num } from '../../lib/format.js';

/**
 * 원본: lottery/category/lottery_category_list.php
 * 원본은 item_type 1 을 "FANZA", 2 를 "ONAHOLE" 로 표시했다(lottery_category_list.php:434-441).
 * 여기서는 한국어로 1 은 굿즈, 2 는 오나홀로 보여준다.
 */
const ITEM_TYPE_KO: Record<string, string> = { '1': '굿즈', '2': '오나홀' };
function itemTypeKo(value: string | null): string | null {
  return value === null ? null : (ITEM_TYPE_KO[value] ?? value);
}

/**
 * 오나홀(2)만 다른 색으로 눈에 띄게 한다 - 나머지는 기본 뱃지색을 쓴다.
 * 처음엔 badge--sky 를 썼는데 기본 뱃지(파란 accent)와 색조가 거의 같아 구분이
 * 안 갔다. badge--memo(보라)는 색상환에서 확실히 떨어져 있어 한눈에 갈린다.
 */
function itemTypeClass(value: string | null): string {
  return value === '2' ? 'badge badge--memo' : 'badge';
}

export function CategoriesPage() {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const page = Number(params.get('page') ?? '1');
  const sort = params.get('sort') ?? 'seq';
  const dir = (params.get('dir') ?? 'desc') as 'asc' | 'desc';

  const update = useUrlUpdate(params, setParams);
  const [search, setSearch] = useUrlSearch(params, update);
  const debounced = params.get('q') ?? '';

  const [formModal, setFormModal] = useState<{ mode: 'create' | 'edit'; category: Category | null } | null>(
    null,
  );

  const query = useQuery({
    queryKey: ['lottery', 'categories', { page, sort, dir, q: debounced }],
    queryFn: () => lotteryApi.categories({ page, perPage: 50, sort, dir, q: debounced || undefined }),
    placeholderData: keepPreviousData,
  });

  return (
    <section>
      <header className="page-head">
        <h1 className="page-title">카테고리 관리</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {query.isFetching && <span className="page-head__busy">갱신 중…</span>}
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => setFormModal({ mode: 'create', category: null })}
          >
            카테고리 등록
          </button>
        </div>
      </header>

      <div className="filters">
        <Field label="검색 (카테고리명 · 코드 · 일본어명)">
          <SearchInput
            value={search}
            placeholder="카테고리명"
            onChange={setSearch}
          />
        </Field>
      </div>

      {query.isPending ? (
        <Spinner />
      ) : query.isError ? (
        <ErrorBox error={query.error} onRetry={() => void query.refetch()} />
      ) : query.data.items.length === 0 ? (
        <EmptyBox />
      ) : (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <SortableTh label="번호" sortKey="seq" currentSort={sort} currentDir={dir} onSort={(k, d) => update({ sort: k, dir: d })} align="right" />
                  <SortableTh label="코드" sortKey="companyCode" currentSort={sort} currentDir={dir} onSort={(k, d) => update({ sort: k, dir: d })} />
                  <SortableTh label="카테고리명" sortKey="companyName" currentSort={sort} currentDir={dir} onSort={(k, d) => update({ sort: k, dir: d })} />
                  <th>일본어명</th>
                  <SortableTh label="구분" sortKey="itemType" currentSort={sort} currentDir={dir} onSort={(k, d) => update({ sort: k, dir: d })} />
                  <th>메모</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((c, i) => (
                  <tr key={c.seq ?? i}>
                    <td className="td-right td-muted">{num(c.seq)}</td>
                    <td><code className="code">{text(c.companyCode)}</code></td>
                    <td>{text(c.companyName)}</td>
                    <td>{text(c.jpName)}</td>
                    <td><span className={itemTypeClass(c.itemType)}>{text(itemTypeKo(c.itemType))}</span></td>
                    <td className="td-muted td-clip">{text(c.memo)}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setFormModal({ mode: 'edit', category: c })}
                      >
                        수정
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={query.data.page}
            totalPages={query.data.totalPages}
            total={query.data.total}
            onChange={(p) => update({ page: p }, false)}
          />
        </>
      )}

      {formModal && (
        <CategoryFormModal
          mode={formModal.mode}
          category={formModal.category}
          onClose={() => setFormModal(null)}
          onSaved={() => {
            setFormModal(null);
            void queryClient.invalidateQueries({ queryKey: ['lottery', 'categories'] });
          }}
        />
      )}
    </section>
  );
}

/**
 * 원본: lottery/category/lottery_category_modify.php 의 modifyexe(수정) +
 * lottery_category_add.php(등록). 두 원본 화면의 폼이 다섯 칸(카테고리명 · 일본어명 ·
 * 코드명 · 유형 · 메모)으로 똑같아서, 여기서는 randombox 상품 수정/복사 모달과 같이
 * mode 하나로 합쳤다 - category 가 null 이면 등록, 있으면 그 값으로 채운 수정이다.
 */
function CategoryFormModal({
  mode,
  category,
  onClose,
  onSaved,
}: {
  mode: 'create' | 'edit';
  category: Category | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    companyName: category?.companyName ?? '',
    jpName: category?.jpName ?? '',
    companyCode: category?.companyCode ?? '',
    itemType: category?.itemType ?? '1',
    memo: category?.memo ?? '',
  });

  const save = useMutation({
    mutationFn: () =>
      mode === 'edit' ? lotteryApi.updateCategory(category!.seq!, form) : lotteryApi.createCategory(form),
    onSuccess: onSaved,
  });

  const valid = form.companyName.trim() !== '' && form.companyCode.trim() !== '';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
          ×
        </button>
        <h2 className="modal-title">{mode === 'edit' ? '카테고리 수정' : '카테고리 등록'}</h2>

        <form
          className="form-grid"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <Field label="카테고리명 *">
            <input
              className="input"
              value={form.companyName}
              onChange={(e) => setForm((f) => ({ ...f, companyName: e.target.value }))}
              required
              maxLength={128}
            />
          </Field>
          <Field label="일본어명">
            <input
              className="input"
              value={form.jpName}
              onChange={(e) => setForm((f) => ({ ...f, jpName: e.target.value }))}
              maxLength={128}
            />
          </Field>
          <Field label="코드명 *">
            <input
              className="input"
              value={form.companyCode}
              onChange={(e) => setForm((f) => ({ ...f, companyCode: e.target.value }))}
              required
              maxLength={40}
            />
          </Field>
          {/* as="div": 라디오 두 개를 감싸는 필드라 기본 <label> 을 쓰면 클릭이
              첫 라디오로만 forwarding 되고 접근성 이름도 뒤섞인다 (Field 주석 참고). */}
          <Field label="랜덤박스 유형" as="div">
            <div className="form-grid" style={{ gridTemplateColumns: 'auto auto', gap: '16px' }}>
              <label className="checkbox">
                <input
                  type="radio"
                  name="itemType"
                  value="1"
                  checked={form.itemType === '1'}
                  onChange={(e) => setForm((f) => ({ ...f, itemType: e.target.value }))}
                />
                <span>굿즈</span>
              </label>
              <label className="checkbox">
                <input
                  type="radio"
                  name="itemType"
                  value="2"
                  checked={form.itemType === '2'}
                  onChange={(e) => setForm((f) => ({ ...f, itemType: e.target.value }))}
                />
                <span>오나홀</span>
              </label>
            </div>
          </Field>
          <Field label="메모" full>
            <input
              className="input"
              value={form.memo}
              onChange={(e) => setForm((f) => ({ ...f, memo: e.target.value }))}
              maxLength={2000}
            />
          </Field>

          {/* 코드명은 상품(lottery_product · fanza_product)이 category_code 로 그대로
              참조한다 - 수정 중에 바꾸면 이 코드를 쓰던 랜덤박스들과의 연결이 끊긴다
              (원본도 같은 위험을 그대로 안고 있었다. 여기서는 알려주기만 한다).
              등록은 원래 코드가 없으니 해당하지 않는다. */}
          {mode === 'edit' && form.companyCode !== (category?.companyCode ?? '') && (
            <p className="hint hint--warn" style={{ gridColumn: '1 / -1' }}>
              코드명을 바꾸면 이 코드를 쓰던 랜덤박스와의 연결이 끊깁니다.
            </p>
          )}

          {save.isError && (
            <div className="qty-msg qty-msg--error">
              {save.error instanceof ApiError ? save.error.message : '저장에 실패했습니다.'}
            </div>
          )}

          <button type="submit" className="btn btn--primary btn--block" disabled={!valid || save.isPending}>
            {save.isPending ? '저장 중…' : mode === 'edit' ? '저장하기' : '등록하기'}
          </button>
        </form>
      </div>
    </div>
  );
}
